import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { X, BookOpen, ChevronDown, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SECTIONS = [
  { key: 'objetivo_tela', label: 'Objetivo da Tela' },
  { key: 'quando_usar', label: 'Quando Usar' },
  { key: 'pre_requisitos', label: 'Pré-requisitos' },
  { key: 'passo_a_passo', label: 'Como Operar' },
  { key: 'campos_e_botoes', label: 'Campos e Botões' },
  { key: 'regras_de_negocio', label: 'Regras Importantes' },
  { key: 'alertas', label: 'Alertas e Cuidados' },
  { key: 'erros_comuns', label: 'Erros Comuns' },
  { key: 'boas_praticas', label: 'Boas Práticas' },
  { key: 'observacoes_finais', label: 'Observações Finais' },
];

const ScreenManualDrawer = ({ screenKey, isOpen, onClose }) => {
  const [manual, setManual] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const loadManual = useCallback(async () => {
    if (!screenKey || !isOpen) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/screen-manuals/by-key/${screenKey}`);
      setManual(res.data);
      if (res.data) {
        const initial = {};
        SECTIONS.forEach(s => { if (res.data[s.key]) initial[s.key] = true; });
        setExpandedSections(initial);
      }
    } catch {
      setManual(null);
    } finally {
      setLoading(false);
    }
  }, [screenKey, isOpen]);

  useEffect(() => { loadManual(); }, [loadManual]);

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div
        className="fixed right-0 top-0 h-full w-full sm:w-[420px] bg-white shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300"
        data-testid="screen-manual-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5EA] bg-[#1C1C1E]">
          <div className="flex items-center gap-2.5">
            <BookOpen className="w-5 h-5 text-[#AF2628]" />
            <h2 className="text-base font-semibold text-white">Manual da Tela</h2>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#2C2C2E] rounded-lg transition-colors" data-testid="close-manual-btn">
            <X className="w-5 h-5 text-[#AEAEB2]" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-8 h-8 border-3 border-[#AF2628] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !manual ? (
            <div className="p-8 text-center">
              <BookOpen className="w-12 h-12 mx-auto mb-3 text-[#E5E5EA]" />
              <p className="text-sm text-[#6E6E73]">Manual ainda não disponível para esta tela.</p>
              <p className="text-xs text-[#AEAEB2] mt-1">Um administrador pode criar o conteúdo na área de Manuais.</p>
            </div>
          ) : (
            <div>
              {/* Title */}
              <div className="px-5 py-4 bg-gradient-to-r from-[#FFF1F0] to-white border-b border-[#E5E5EA]">
                <h3 className="text-lg font-bold text-[#111111]">{manual.nome_tela}</h3>
                <div className="flex items-center gap-3 mt-2 text-[11px] text-[#AEAEB2]">
                  <span>v{manual.versao}</span>
                  <span>Atualizado: {new Date(manual.atualizado_em).toLocaleDateString('pt-BR')}</span>
                  {manual.atualizado_por_nome && <span>por {manual.atualizado_por_nome}</span>}
                </div>
              </div>

              {/* Sections */}
              <div className="divide-y divide-[#E5E5EA]">
                {SECTIONS.map(section => {
                  const content = manual[section.key];
                  if (!content) return null;
                  const isExpanded = expandedSections[section.key];
                  return (
                    <div key={section.key}>
                      <button
                        onClick={() => toggleSection(section.key)}
                        className="w-full flex items-center justify-between px-5 py-3 hover:bg-[#F7F7F8] transition-colors"
                        data-testid={`manual-section-${section.key}`}
                      >
                        <span className="text-sm font-semibold text-[#111111]">{section.label}</span>
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-[#AEAEB2]" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-[#AEAEB2]" />
                        )}
                      </button>
                      {isExpanded && (
                        <div className="px-5 pb-4 text-sm text-[#3A3A3C] leading-relaxed whitespace-pre-wrap">
                          {content}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ScreenManualDrawer;
